import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const accountingMethodCashVsAccrualGuide: AdminGuide = {
  id: "accounting-method-cash-vs-accrual",
  title:
    "Accounting method (cash vs accrual): what it changes, what it doesn’t, and switching safely",
  category: "Provider Sync & Data",
  audience: ["Support", "Ops", "Finance", "CS"],
  summary:
    "Explain the accounting basis choice in plain English and avoid unsafe basis changes that trigger provider data refresh.",
  keywords: [
    "accounting basis",
    "cash basis",
    "accrual basis",
    "profit and loss",
    "cashflow",
    "refresh",
    "resync",
    "showAccural",
  ],
  location: "CustomerApp",
  risk: "high",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.ACCOUNTING_STANDARDS,
    GUIDE_SOURCE_DOCS.POSTPAYMENT_UAT,
  ],
  warnings: [
    "High impact: changing the accounting method triggers a data refresh/re-sync. Provider-derived snapshots are purged and rebuilt from the accounting provider.",
    "Do not recommend switching basis casually. If the customer is unsure, pause and escalate to a senior support lead or Engineering.",
  ],
  whatThisHelpsYouDo: [
    "Explain cash vs accrual in plain English.",
    "Set correct expectations about what changes and what does not.",
    "Avoid accidental refreshes that surprise customers.",
  ],
  whenToUse: [
    "A customer is onboarding and must choose an accounting basis.",
    "A customer asks why Profit & Loss differs from cashflow.",
    "A customer asks to switch their accounting method.",
  ],
  whenNotToUse: [
    "You do not understand what the customer is trying to achieve (ask clarifying questions first).",
    "The customer is mid-critical work and switching would disrupt them (schedule it).",
  ],
  beforeYouStart: [
    "Ask what they want reports to reflect (cash received/paid vs invoices/bills earned/incurred).",
    "Confirm which provider they use (Xero or QuickBooks).",
    "Confirm they understand switching basis can trigger a refresh/re-sync.",
  ],
  steps: [
    "Explain the difference in one sentence: cash basis recognises when money moves; accrual recognises when income/expenses are earned/incurred.",
    "Explain what it changes in HelloCashflow: it changes the basis used for income/expense reporting (Profit & Loss-style views).",
    "Explain what it does NOT change: cashflow views are bank movement and remain cash movement.",
    "If the customer is onboarding, have them choose the basis during the setup wizard step “Accounting Basis”.",
    "If the customer wants to switch later: warn that changing basis triggers a refresh/re-sync (provider-derived snapshots are purged and rebuilt).",
    "If they still want to proceed, schedule it and define what “success” looks like after the refresh (sync completes and reports repopulate).",
    "If anything is unclear, stop and escalate with the organisationId and the customer’s goal.",
  ],
  whatSuccessLooksLike: [
    "Customer chooses the correct basis and understands the impact.",
    "Customer is not surprised by the effects of switching (refresh/re-sync).",
    "After a switch (if performed), reports align to the chosen method once sync completes.",
  ],
  troubleshooting: [
    {
      title: "Customer says “cashflow is wrong after I changed basis”",
      body: [
        "Clarify that cashflow is bank movement and should not be governed by the accounting basis choice.",
        "If cashflow changed unexpectedly, escalate with organisationId and timestamps (may be a sync issue).",
      ],
    },
    {
      title: "Customer switched basis and now reports look empty/stale",
      body: [
        "Ask whether a refresh/re-sync is in progress or failed.",
        "Collect: organisationId, provider (Xero/QB), and when the change was made.",
        "Escalate if data does not repopulate after a reasonable window.",
      ],
    },
  ],
  escalateWhen: [
    "Customer is unsure which basis to choose and it affects critical decisions.",
    "Customer wants to switch basis but you cannot confirm the impact.",
    "After switching, provider-derived data does not repopulate or sync appears stuck.",
  ],
  relatedGuides: [
    { label: "Onboarding support", guideId: "onboarding-support" },
    {
      label: "Sync an organisation's accounting data",
      guideId: "provider-resync-and-overrides",
    },
  ],
  relatedAdminPaths: [{ label: "Organisations", path: "/organisations" }],
};
